import { Button, FormControl, Grid, TextField } from "@mui/material";
import { GridActionsCellItem
, GridColDef } from "@mui/x-data-grid";
import DataTable from "../../../../../common/components/dataTable";
import FormHeader from "../../../../../common/components/forms/formHeader";
import CellphoneTextField from "../../../../../common/components/input/cellphoneTextField";
import SelectStyled from "../../../../../common/components/input/selectStyled";
import TelephoneTextField from "../../../../../common/components/input/telephoneTextField";
import { FormEvent, useMemo, useState } from "react";
import { ContactProfile } from "../../../../../common/types/contactProfile";
import { Delete as DeleteIcon } from "@material-ui/icons";

type Props = {
  initialValues?: ContactProfile[];
  addCallback: (newContact: ContactProfile) => void;
  removeCallback: (id: number) => void;
  submitContatos: () => void;
};

function NewClienteContatosForm({
  initialValues,
  addCallback,
  removeCallback,
  submitContatos,
}: Props) {
  const [formState, setFormState] = useState<ContactProfile>();

  const rows: {
    id?: number;
    nome?: string;
    telefone?: string;
    celular?: string;
    email?: string;
  }[] = useMemo(() => {
    return (
      initialValues?.map((contato) => {
        return {
          id: contato.id,
          nome: contato.dadosPessoais?.nome,
          telefone: contato.contato?.telefone,
          celular: contato.contato?.celular,
          email: contato.contato?.email,
        };
      }) ?? []
    );
  }, [initialValues]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "nome",
        headerName: "Nome",
        flex: 1,
        headerClassName: "header-theme",
      },
      {
        field: "telefone",
        headerName: "Telefone",
        flex: 1,
        headerClassName: "header-theme",
      },
      {
        field: "celular",
        headerName: "Celular",
        flex: 1,
        headerClassName: "header-theme",
      },
      {
        field: "email",
        headerName: "E-mail",
        flex: 1,
        headerClassName: "header-theme",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Ações",
        headerClassName: "header-theme",
        // @ts-ignore
        getActions: (params: any) => [
          <GridActionsCellItem
            placeholder=""
            icon={<DeleteIcon color="error" />}
            label="Excluir"
            onClick={() => handleRemoveContact(params.id)}
          />,
        ],
      },
    ];
  }, []);

  const handleAddContact = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const maxId =
      initialValues?.reduce(
        (acc, curr) => (curr.id! > acc ? curr.id! : acc),
        0
      ) ?? 0;

    const newContact = {
      ...formState,
      id: maxId + 1,
    };

    addCallback(newContact);
    setFormState(undefined);
  };

  const handleRemoveContact = (id: number) => {
    removeCallback(id);
  };

  return (
    <>
      <form onSubmit={handleAddContact}>
        <Grid container spacing={2} xs={12} marginTop={2}>
          <Grid item xs={6}>
            <FormControl fullWidth required>
              <TextField
                label="Nome Completo"
                value={formState?.dadosPessoais?.nome ?? ""}
                size="small"
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    dadosPessoais: {
                      ...formState?.dadosPessoais,
                      nome: e.target.value,
                    },
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth required>
              <TextField
                label="Nome Resumido"
                value={formState?.dadosPessoais?.nomeResumido ?? ""}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    dadosPessoais: {
                      ...formState?.dadosPessoais,
                      nomeResumido: e.target.value,
                    },
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <SelectStyled
                options={["Feminino", "Masculino", "Outro"].map(
                  (genero, index) => ({
                    id: index,
                    value: genero,
                  })
                )}
                onChangeAction={(e, value) => {
                  setFormState({
                    ...formState,
                    genero: value?.value,
                  });
                }}
                label={"Gênero"}
              ></SelectStyled>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                required
                type={"tel"}
                label="Telefone"
                size="small"
                value={formState?.contato?.telefone ?? ""}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    contato: {
                      ...formState?.contato,
                      telefone: e.target.value,
                    },
                  })
                }
                InputProps={{
                  inputComponent: TelephoneTextField as any,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                required
                type={"tel"}
                label="Celular"
                size="small"
                value={formState?.contato?.celular ?? ""}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    contato: {
                      ...formState?.contato,
                      celular: e.target.value,
                    },
                  })
                }
                InputProps={{
                  inputComponent: CellphoneTextField as any,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <TextField
                required
                type={"email"}
                label="E-mail"
                size="small"
                value={formState?.contato?.email ?? ""}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    contato: {
                      ...formState?.contato,
                      email: e.target.value,
                    },
                  })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} textAlign="right">
            <Button variant="contained" type="submit" color="secondary">
              Adicionar
            </Button>
          </Grid>
        </Grid>
      </form>
      <Grid container spacing={2}>
        <FormHeader>Contatos Adicionados</FormHeader>
        <Grid item xs={12}>
          <DataTable columns={columns} rows={rows}></DataTable>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} textAlign="right" paddingTop={2}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => submitContatos()}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export default NewClienteContatosForm;
